import { Web3Provider } from "@ethersproject/providers";
import { Web3ReactProvider } from "@web3-react/core";
import { AxiosError } from "axios";
import { Suspense, lazy, useContext, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { SWRConfig } from "swr";
import { AppContainer } from "./App.style";
import AccessGate from "./components/AccessGate";
import CommandModal from "./components/CommandModal";
import InvalidReferralModal from "./components/InvalidReferralModal";
import LiquidationModal from "./components/LiquidationModal";
import LoadingScreen from "./components/LoadingScreen";
import MMPTriggeredModal from "./components/MMPTriggeredModal";
import OFACModal from "./components/OFACModal";
import { Toast } from "./components/shared/Toast";
import UpdateAvailableBanner from "./components/UpdateAvailableBanner";
import { BUILD_VERSION, BUILD_TIME, COMMIT_HASH } from "./buildInfo";
import { AccountContextProvider } from "./contexts/AccountContext";
import { AuthContext, AuthContextProvider } from "./contexts/AuthContext";
import {
  ConnectWalletContext,
  ConnectWalletContextProvider,
} from "./contexts/ConnectWalletContext";
import {
  IntercomContext,
  IntercomContextProvider,
} from "./contexts/IntercomContext";
import { MarketContextProvider } from "./contexts/MarketContext";
import { NotificationContextProvider } from "./contexts/NotificationContext";
import { SettingsContextProvider } from "./contexts/SettingsContext";
import { ToastContextProvider } from "./contexts/ToastContext";
import { ErrorDialogContextProvider } from "./contexts/ErrorDialogContext";
import { WebsocketContextProvider } from "./contexts/WebsocketAuthContext";
import { RealtimeErrorBoundary } from "./contexts/WebsocketAuthContext/RealtimeErrorBoundary";
import { LocalStorageKeyEnum } from "./enums/localStorage";
import { useAnalytics } from "./hooks/api/analytics/useAnalytics";
import useScreenSize from "./hooks/screenSize/useScreenSize";
import useEagerConnect from "./hooks/wallet/useEagerConnect";
import MarginXRoutes from "./routes";
import { KeyboardProvider } from "keyboard/KeyboardProvider";
import { ShortcutsModal } from "./components/keyboard/ShortcutsModal";
import { MarginxKeyboardBindings } from "./components/keyboard/MarginxKeyboardBindings";
import { ChordOverlay } from "./components/keyboard/ChordOverlay";
import { nanosToMillis } from "./utils/date";
import { INVITE_ACCESS_ENABLED } from "./utils/env";
import { allConnectors } from "./utils/wallet/connectors";
import { WalletProviderGate } from "./components/WalletProviderGate";
import { WalletBridge } from "./components/WalletBridge";
import { useAutomaticFaucet } from "./hooks/api/faucet/useAutomaticFaucet";
import FaucetOnboardingNotifier from "./components/FaucetOnboardingNotifier";

const LazyIntercomSetupModal = lazy(
  () => import("./components/IntercomSetupModal/")
);

export function getLibrary(provider: any): Web3Provider {
  const library = new Web3Provider(provider, "any");
  library.pollingInterval = 15000;
  return library;
}

if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.info(
    `%c[app] v${BUILD_VERSION} • ${COMMIT_HASH} • built ${BUILD_TIME}`,
    "color:#9b8cff"
  );
  (window as any).__APP_VERSION__ = {
    version: BUILD_VERSION,
    commit: COMMIT_HASH,
    buildTime: BUILD_TIME,
  };
}

function Root() {
  useEagerConnect();
  useAnalytics();
  useAutomaticFaucet();

  const { account } = useContext(AuthContext);
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const { showIntercomSetupModal } = useContext(IntercomContext);
  const { height } = useScreenSize();
  useEffect(() => {
    if (
      !account &&
      window.localStorage.getItem(LocalStorageKeyEnum.REFERRAL_CODE)
    ) {
      setShowConnectModal(true);
    }
  }, [account, setShowConnectModal]);

  const appContent = (
    <>
      {account && (
        <div>
          <InvalidReferralModal />
          <LiquidationModal />
          <MMPTriggeredModal />
        </div>
      )}
      {showIntercomSetupModal && (
        <Suspense>
          <LazyIntercomSetupModal />
        </Suspense>
      )}
      <OFACModal />
      <CommandModal />
      <MarginXRoutes />
    </>
  );

  return (
    <AppContainer style={{ height }}>
      <Toast />
      <FaucetOnboardingNotifier />
      <UpdateAvailableBanner />
      {/*
        v7 flags opted into early, which is also what the test renderer already
        does (see test-utils/renderWithProviders), so tests and app finally agree.

        `v7_relativeSplatPath` is a no-op here and was verified as such rather
        than assumed: this app has exactly one splat route (routes/index.tsx,
        `path: "*"`), and its element navigates to an ABSOLUTE path, so there is
        no relative resolution for the flag to change.
      */}
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <KeyboardProvider>
          {INVITE_ACCESS_ENABLED ? (
            <AccessGate>{appContent}</AccessGate>
          ) : (
            appContent
          )}
          <MarginxKeyboardBindings />
          <ShortcutsModal />
          <ChordOverlay />
        </KeyboardProvider>
        {/* <LoadingScreen autoHideAfterMs={100} /> */}
      </BrowserRouter>
    </AppContainer>
  );
}

function App() {
  // Everything below the realtime provider. Rendered inside the provider
  // normally, and without it if the realtime layer throws (RealtimeErrorBoundary),
  // so a realtime failure degrades to REST polling instead of a blank page.
  const realtimeConsumers = (
    <SWRConfig
      value={{
        shouldRetryOnError: true,
        onErrorRetry: (err, _, __, revalidate, { retryCount }) => {
          const axiosErr = err as AxiosError | undefined;
          if (axiosErr?.response?.status === 429) {
            // Cap retries so a rate-limited burst can't feed
            // itself into an infinite retry storm.
            const MAX_RETRIES = 8;
            if (retryCount >= MAX_RETRIES) {
              return;
            }
            // Retry mechanism on rate limited.
            // Honor the server's retry_after when present;
            // otherwise fall back to jittered exponential
            // backoff (1s, 2s, 4s, ... capped at 30s).
            const retryAfterNanos: string | undefined =
              //@ts-ignore
              axiosErr?.response?.data?.retry_after;
            const backoffMillis = Math.min(1000 * 2 ** retryCount, 30000);
            const jitterMillis = Math.round(backoffMillis * 0.2 * Math.random());
            const retryAfterMillis = retryAfterNanos
              ? nanosToMillis(retryAfterNanos)
              : backoffMillis + jitterMillis;
            setTimeout(() => revalidate({ retryCount }), retryAfterMillis);
          }
        },
        onError: (error) => {
          if (error.status !== 429 && error.status !== 401) {
            // do nothing
          }
        },
      }}
    >
      <AccountContextProvider>
        <Root />
      </AccountContextProvider>
    </SWRConfig>
  );

  return (
    <SettingsContextProvider>
      <ConnectWalletContextProvider>
        <IntercomContextProvider>
          <ToastContextProvider>
          <ErrorDialogContextProvider>
              <MarketContextProvider>
                <WalletProviderGate>
                <Web3ReactProvider connectors={allConnectors}>
                  <WalletBridge />
                  <AuthContextProvider>
                    <NotificationContextProvider>
                      <RealtimeErrorBoundary fallback={realtimeConsumers}>
                        <WebsocketContextProvider>
                          {realtimeConsumers}
                        </WebsocketContextProvider>
                      </RealtimeErrorBoundary>
                    </NotificationContextProvider>
                  </AuthContextProvider>
                </Web3ReactProvider>
                </WalletProviderGate>
              </MarketContextProvider>
          </ErrorDialogContextProvider>
          </ToastContextProvider>
        </IntercomContextProvider>
      </ConnectWalletContextProvider>
    </SettingsContextProvider>
  );
}

export default App;
